import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  BookOpen,
  Send,
  Store,
  Shield,
  Settings,
  UserCog,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  ShoppingBag,
  MessageCircle,
  Headphones,
  Gift,
} from 'lucide-react';
import { useI18n } from '../../i18n/useI18n';

export default function Sidebar({ onCloseMobile, collapsed, onToggleCollapse }) {
  const { t } = useI18n();

  const NAV_GROUPS = useMemo(
    () => [
      {
        label: t('nav.pilotage'),
        items: [{ label: t('nav.dashboard'), path: '/', icon: LayoutDashboard }],
      },
      {
        label: t('nav.gestion'),
        items: [
          { label: t('nav.members'), path: '/members', icon: Users },
          { label: t('nav.finance'), path: '/finance', icon: CreditCard },
          { label: t('nav.rewards'), path: '/rewards', icon: Gift },
          { label: t('nav.content'), path: '/library', icon: BookOpen },
          { label: t('nav.administrators'), path: '/administrators', icon: UserCog },
        ],
      },
      {
        label: t('nav.ecosysteme'),
        items: [
          { label: t('nav.boutique'), path: '/boutique', icon: ShoppingBag },
          { label: t('nav.marketplace'), path: '/marketplace', icon: Store },
          { label: t('nav.community'), path: '/community', icon: MessageCircle },
          { label: 'Support', path: '/community/support', icon: Headphones },
          { label: t('nav.communication'), path: '/communication', icon: Send },
        ],
      },
      {
        label: t('nav.systeme'),
        items: [
          { label: t('nav.settings'), path: '/settings', icon: Settings },
          { label: t('nav.configuration'), path: '/security', icon: Shield },
        ],
      },
    ],
    [t]
  );

  return (
    <aside
      className={`${collapsed ? 'w-[76px]' : 'w-64'} min-h-screen p-3 flex flex-col justify-between shrink-0 transition-all duration-200`}
      style={{
        background: 'var(--sidebar-bg, #fff)',
        color: 'var(--text-secondary, #64748b)',
        borderRight: '1px solid var(--border, #e2e8f0)',
      }}
    >
      <div className="space-y-5">
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'} px-1 py-1`}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-indigo-600 text-white rounded-xl shadow-sm shadow-indigo-500/20 shrink-0">
              <Shield size={18} strokeWidth={2.5} />
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <h1 className="font-bold text-sm tracking-tight leading-none truncate" style={{ color: 'var(--text-primary, #0f172a)' }}>
                  DSI Academy
                </h1>
                <span className="text-[10px] font-semibold text-indigo-500 tracking-wider uppercase">
                  Admin Control
                </span>
              </div>
            )}
          </div>
          {onCloseMobile && (
            <button onClick={onCloseMobile} className="lg:hidden p-1" style={{ color: 'var(--text-muted, #94a3b8)' }}>
              <X size={18} />
            </button>
          )}
        </div>

        <nav className="space-y-4">
          {NAV_GROUPS.map((group) => (
            <div key={group.label}>
              {!collapsed && (
                <p className="px-2 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: 'var(--text-muted, #94a3b8)' }}>
                  {group.label}
                </p>
              )}
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={`${item.path}-${item.label}`}
                      to={item.path}
                      end={item.path === '/'}
                      onClick={onCloseMobile}
                      title={item.label}
                      className={({ isActive }) =>
                        `relative flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-2.5 py-2 rounded-xl text-[13px] font-medium transition-all duration-200 ${
                          isActive
                            ? 'bg-indigo-50 text-indigo-700'
                            : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-indigo-600" />}
                          <Icon size={18} strokeWidth={2} />
                          {!collapsed && <span className="truncate">{item.label}</span>}
                        </>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </div>

      <div className="pt-3 space-y-2">
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            className="w-full flex items-center justify-center gap-2 rounded-xl py-2 text-xs font-semibold transition border border-slate-200 text-slate-600"
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
            {!collapsed && <span>Menu</span>}
          </button>
        )}
      </div>
    </aside>
  );
}
